import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from "axios";
import { getApiUrl } from "./env";

let _apiInstance: AxiosInstance | null = null;
let tokenGetter: (() => Promise<string | null>) | null = null;

function getApiInstance(): AxiosInstance {
    if (!_apiInstance) {
        const apiUrl = getApiUrl();

        _apiInstance = axios.create({
            baseURL: apiUrl,
            headers: {
                "Content-Type": "application/json",
            },
            withCredentials: true,
        });

        _apiInstance.interceptors.response.use(
            (response) => response,
            async (error: AxiosError) => {
                const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

                if (error.response?.status === 401 && !originalRequest._retry && tokenGetter) {
                    originalRequest._retry = true;

                    try {
                        const newToken = await tokenGetter();

                        if (newToken && _apiInstance) {
                            setAuthToken(newToken);

                            if (originalRequest.headers) {
                                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                            }
                            return _apiInstance(originalRequest);
                        }
                    } catch (refreshError) {
                        console.error('Failed to refresh token:', refreshError);
                        if (typeof window !== 'undefined') {
                            window.location.href = '/sign-in';
                        }
                    }
                }

                return Promise.reject(error);
            }
        );
    }
    return _apiInstance;
}

export const api = new Proxy({} as AxiosInstance, {
    get(_target, prop) {
        const instance = getApiInstance();
        const value = instance[prop as keyof AxiosInstance];
        return typeof value === 'function' ? value.bind(instance) : value;
    },
});

export const setAuthToken = (token: string | null) => {
    const instance = getApiInstance();
    if (token) {
        instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
        delete instance.defaults.headers.common['Authorization'];
    }
};

export const setTokenGetter = (getter: () => Promise<string | null>) => {
    tokenGetter = getter;
};

// ==================== Subscription API ====================

export interface SubscriptionStatusResponse {
    plan: "free" | "pro";
    status: "active" | "trialing" | "past_due" | "canceled" | "canceling" | null;
    subscription_id: string | null;
    period_end: string | null;
    is_pro: boolean;
    is_canceling?: boolean;
}

export interface CheckoutSessionResponse {
    checkout_url: string;
    session_id: string;
}

export const subscriptionApi = {
    getStatus: () => api.get<SubscriptionStatusResponse>("/api/subscription/status"),
    createCheckoutSession: () => api.post<CheckoutSessionResponse>("/api/subscription/checkout"),
    cancelSubscription: () => api.post<{ message: string; subscription_id: string }>("/api/subscription/cancel"),
    resubscribe: () => api.post<{ message: string; subscription_id: string }>("/api/subscription/resubscribe"),
};

// ==================== Health API ====================

export interface HealthStatusResponse {
    status: "ok" | "unhealthy";
    timestamp: string;
    uptime: number;
}

export const healthApi = {
    getStatus: () => api.get<HealthStatusResponse>("/health"),
};